import { PRODUCT_NAME } from '../constants/brand';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { getAppointmentGuidePosition } from './appointmentGuideLayout';
import { getGuidePosition } from './guideLayout';

// Presentation only: locating/scrolling a target never invokes its actions.
export default function GuideOverlay({ step, index, total, onBack, onNext, onClose }) {
  const [rect, setRect] = useState(null);
  const panel = useRef(null);
  const explanation = useRef(null);
  const [panelHeight, setPanelHeight] = useState(260);
  const [viewport, setViewport] = useState({ width: window.innerWidth, height: window.innerHeight });
  const appointmentLayout = step.layout === 'appointment-detail';
  const touchY = useRef(null);
  const scrollExample = delta => {
    if (!appointmentLayout) return;
    const target = document.querySelector(step.target);
    const content = target?.querySelector('.MuiDialogContent-root') || target;
    if (content) content.scrollTop += delta;
  };
  useLayoutEffect(() => {
    const previousFocus = document.activeElement;
    panel.current?.focus({ preventScroll: true });
    return () => previousFocus?.focus?.({ preventScroll: true });
  }, []);
  useLayoutEffect(() => {
    if (step.target) window.dispatchEvent(new CustomEvent('profile-reveal-target', { detail: { target: step.target } }));
    let scrolledTarget;
    const sizeObserver = new ResizeObserver(() => update());
    const update = () => {
      setViewport(previous => previous.width === window.innerWidth && previous.height === window.innerHeight ? previous : { width: window.innerWidth, height: window.innerHeight });
      const element = step.target ? document.querySelector(step.target) : null;
      if (!element) { setRect(null); return; }
      if (element.closest('[data-profile-section][hidden]')) {
        window.dispatchEvent(new CustomEvent('profile-reveal-target', { detail: { target: step.target } }));
        setRect(null);
        return;
      }
      if (scrolledTarget !== element) {
        sizeObserver.disconnect();
        sizeObserver.observe(element);
        scrolledTarget = element;
        element.scrollIntoView?.({ block: 'center', inline: 'nearest', behavior: 'instant' });
        // A large real panel may leave two unusably small gaps when centered.
        // Align it to the bottom once so the explanation can sit above it.
        const centered = element.getBoundingClientRect();
        if (!appointmentLayout && centered.height <= window.innerHeight - 192 &&
          centered.top < 192 && window.innerHeight - centered.bottom < 192 &&
          centered.left < 412 && window.innerWidth - centered.right < 412) {
          element.scrollIntoView?.({ block: 'end', inline: 'nearest', behavior: 'instant' });
        }
      }
      const bounds = element.getBoundingClientRect();
      const next = { left: bounds.left, top: bounds.top, bottom: bounds.bottom, width: bounds.width, height: bounds.height };
      setRect(previous => previous && Object.keys(next).every(key => previous[key] === next[key]) ? previous : next);
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      observer.disconnect();
      sizeObserver.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [step.target, appointmentLayout]);
  useLayoutEffect(() => {
    if (!panel.current) return undefined;
    const measure = () => {
      const overflow = explanation.current ? explanation.current.scrollHeight - explanation.current.clientHeight : 0;
      setPanelHeight((panel.current?.offsetHeight || 260) + overflow);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(panel.current);
    if (explanation.current) observer.observe(explanation.current);
    return () => observer.disconnect();
  }, [step]);
  const position = getGuidePosition(rect, viewport.width, viewport.height, panelHeight);
  const appointmentPosition = appointmentLayout && rect ? getAppointmentGuidePosition(rect, viewport.width, viewport.height, panelHeight) : {};
  return <Box data-guide-overlay sx={{ position: 'fixed', inset: 0, zIndex: 15000 }}>
    <Box data-guide-backdrop onWheel={event => scrollExample(event.deltaY)} onTouchStart={event => { touchY.current = event.touches[0]?.clientY; }} onTouchMove={event => {
      const y = event.touches[0]?.clientY;
      if (touchY.current != null && y != null) scrollExample(touchY.current - y);
      touchY.current = y;
    }} onTouchEnd={() => { touchY.current = null; }} sx={{ position: 'absolute', inset: 0, touchAction: appointmentLayout ? 'none' : undefined, bgcolor: rect ? 'transparent' : 'rgba(15,23,42,.56)' }} />
    {rect && <Box sx={{ pointerEvents: 'none', position: 'fixed', left: rect.left - 6, top: rect.top - 6, width: rect.width + 12, height: rect.height + 12, borderRadius: 2, boxShadow: '0 0 0 9999px rgba(15,23,42,.56)', border: '2px solid #5EEAD4' }} />}
    <Box ref={panel} tabIndex={-1} role="dialog" aria-label={step.title} aria-modal="true" onKeyDown={event => {
      if (event.key === 'Escape') { event.stopPropagation(); onClose(); }
      if (event.key !== 'Tab') return;
      const buttons = [...panel.current.querySelectorAll('button:not(:disabled)')];
      const current = buttons.indexOf(document.activeElement);
      event.preventDefault();
      buttons[(current + (event.shiftKey ? -1 : 1) + buttons.length) % buttons.length]?.focus();
    }} sx={{ position: 'fixed', ...position, width: 'min(380px, calc(100vw - 32px))', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 2.5, bgcolor: 'white', borderRadius: 3, boxShadow: '0 18px 50px rgba(15,23,42,.28)', ...(appointmentLayout ? { ...appointmentPosition, maxHeight: 'calc(100vh - 32px)' } : {}) }}>
      <Box ref={explanation} sx={{ overflowY: 'auto', minHeight: 0, overscrollBehavior: 'contain' }}>
      <Typography variant="caption" color="#0F766E" fontWeight={800}>{PRODUCT_NAME} TOUR · {index + 1}/{total}</Typography>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1 }}>{step.title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{step.body}</Typography>
      {!step.completion && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>Walkthrough only — {index === total - 1 ? 'use Got it to finish' : 'use Next to continue'}. Highlighted controls are not interactive during the tour.</Typography>}
      </Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 2, flexShrink: 0 }}>
        <Button onClick={onClose} color="inherit">{step.completion ? 'Later' : 'Skip'}</Button>
        <Stack direction="row" spacing={1}>
          <Button onClick={onBack} disabled={index === 0} color="inherit">Back</Button>
          <Button onClick={onNext} variant="contained" sx={{ bgcolor: '#29C0BB' }}>{step.nextLabel || (index === total - 1 ? 'Got it' : 'Next')}</Button>
        </Stack>
      </Stack>
    </Box>
  </Box>;
}
